/**
 * Identidade do aparelho — o valor que viaja como `fingerprint` no pedido e no
 * cadastro de cartão.
 *
 * ## Por que este módulo existe
 *
 * Até 08/09/2026 o `FingerprintService` fazia `await import('@fingerprintjs/…')`
 * a cada visita, sem `catch`. Medido na semana de 01–08/09: **74 devices** (54
 * em Safari iOS) tiveram esse `import()` falhando com `ChunkLoadError` — o chunk
 * existia na borda, a retentativa rodou três vezes e falhou nas três; a
 * assinatura é de bloqueador de conteúdo ou da proteção antifingerprinting do
 * Safari, que casam a URL pelo nome. E a falha não ficava no serviço: subia ao
 * `ErrorHandler`, que por ser `ChunkLoadError` **recarregava o cardápio** — por
 * uma biblioteca que só serve na hora de enviar o pedido.
 *
 * ## O contrato
 *
 * 1. **Um id por aparelho, gravado uma vez, lido do `localStorage` depois.** O
 *    fingerprintjs recalcula o mesmo valor a cada visita, mas o `import()` custa
 *    ~100 KB e uma requisição; com cache, ele roda no máximo uma vez por device.
 * 2. **Se o fingerprintjs falhar, um id ALEATÓRIO faz o mesmo papel** — gerado
 *    uma vez, gravado, reusado enquanto existir. Mesmo formato (32 hex), para
 *    nada a jusante mudar de forma.
 * 3. **Nunca trocar um id já gravado.** Se o device caiu no aleatório, fica no
 *    aleatório: tentar o fingerprintjs na visita seguinte e substituir faria o
 *    mesmo aparelho aparecer como dois. Estabilidade vale mais que o valor
 *    "verdadeiro".
 * 4. **Nunca lança, nunca rejeita.** O que der errado (import bloqueado, `load`
 *    que lança, `localStorage` que lança em webview com cookies bloqueados)
 *    vira fallback em memória — jamais chega ao `ErrorHandler`.
 * 5. **Prazo NÃO grava.** Um `import()` pendurado (o loader do webpack só
 *    desiste em 120 s) não pode segurar o envio do pedido: passado `prazoMs`,
 *    resolve com um aleatório **só em memória**. Se o fingerprintjs chegar
 *    depois e nada tiver sido gravado, é ele que fica no storage. Lentidão não
 *    é bloqueio — medido nas 2 primeiras horas em produção (08/09/2026): 73
 *    fallbacks, TODOS por prazo, zero por import; 69 em Android, onde o
 *    fingerprintjs tem p90 de 4 s e p99 de 7,5 s, e 3 em iOS com o timer
 *    disparando aos 227 s (aba em segundo plano). Gravar o aleatório ali
 *    carimbaria ~13% dos Android para sempre por serem lentos.
 *
 * A ORIGEM do valor (`fingerprintjs` | `aleatorio`) fica gravada junto e sai na
 * telemetria (`ClientFingerprintResolved`), não no payload: o id mantém o
 * formato de sempre, e quem quiser distinguir as populações no backend precisa
 * de um campo novo combinado antes — ver a nota no `FingerprintService`.
 *
 * Limite conhecido: o Safari apaga `localStorage` de site sem interação por 7
 * dias (ITP). O fingerprintjs sobrevive a isso porque recalcula o mesmo valor;
 * o aleatório não — usuário esporádico de Safari no fallback vira device novo a
 * cada retorno. Aceito e registrado; é justamente a população do fallback.
 *
 * Puro de propósito: recebe storage, carregador e gerador por parâmetro, então
 * o spec exercita os três caminhos (cache, fingerprintjs, aleatório) sem
 * TestBed, sem rede e sem relógio.
 */

/** Chave no `localStorage`. */
export const CHAVE_FINGERPRINT = 'mp:fingerprint';

/** Prazo para o fingerprintjs responder antes de cair no aleatório. */
export const PRAZO_FINGERPRINT_MS = 10000;

/** Só o que este módulo usa de `localStorage` — facilita o teste. */
export interface StorageMinimo {
  getItem(key: string): string | null;
  setItem(key: string, value: string): any;
}

/** De onde veio o id GRAVADO. */
export type OrigemFingerprint = 'fingerprintjs' | 'aleatorio';

/** O que fica no `localStorage`. */
export interface RegistroFingerprint {
  id: string;
  origem: OrigemFingerprint;
  /** Epoch ms de quando foi gerado — idade do id, para a telemetria. */
  ts: number;
}

/**
 * Como a resolução terminou. `origem: 'cache'` é o caminho quente (lido do
 * storage); os outros dois geraram id nesta chamada e merecem um evento.
 */
export interface ResolucaoFingerprint {
  id: string;
  origem: 'cache' | OrigemFingerprint;
  /**
   * Por que caiu no aleatório: `import` (o chunk não carregou — bloqueio ou
   * rede), `load` (`FingerprintJS.load()` lançou/rejeitou), `get` (`fp.get()`
   * lançou/rejeitou), `vazio` (resolveu sem `visitorId`), `prazo` (estourou
   * `prazoMs`), `carregador` (o carregador em si lançou síncrono). Ausente
   * quando não houve fallback. `tardio` só aparece no callback
   * `aoGravarTardio`: o fingerprintjs chegou depois do prazo e foi gravado.
   */
  motivo?: string;
  /** `name` do erro que causou o fallback, quando houve um. */
  erro_classe?: string;
  /** Quanto tempo a resolução levou, em ms. */
  ms: number;
  /** O registro foi gravado no storage? `false` = só em memória (storage falhou). */
  persistido: boolean;
}

export interface DepsResolucao {
  storage: StorageMinimo | null;
  /** Carrega o fingerprintjs e devolve o `visitorId`. Pode lançar ou rejeitar. */
  carregar: () => Promise<string>;
  /** Gera um id aleatório de 32 hex. */
  gerar?: () => string;
  agora?: () => number;
  prazoMs?: number;
  agendar?: (fn: () => void, ms: number) => any;
  cancelar?: (handle: any) => void;
  /**
   * Chamado quando o fingerprintjs chega DEPOIS do prazo e é gravado (a
   * Promise já resolveu com o aleatório em memória). Serve à telemetria; a
   * sessão corrente continua com o id que já devolveu.
   */
  aoGravarTardio?: (r: ResolucaoFingerprint) => void;
}

/** Formato aceito para um id gravado: não-vazio, sem espaço, até 64 chars. */
const ID_VALIDO = /^[A-Za-z0-9_-]{8,64}$/;

/** Lê o registro do storage. Qualquer coisa fora do formato vale `null`. */
export function lerRegistro(storage: StorageMinimo | null): RegistroFingerprint | null {
  try {
    if (!storage) return null;
    const cru = storage.getItem(CHAVE_FINGERPRINT);
    if (!cru) return null;
    const p = JSON.parse(cru);
    if (!p || typeof p.id !== 'string' || !ID_VALIDO.test(p.id)) return null;
    const origem: OrigemFingerprint = p.origem === 'aleatorio' ? 'aleatorio' : 'fingerprintjs';
    const ts = typeof p.ts === 'number' && isFinite(p.ts) ? p.ts : 0;
    return { id: p.id, origem, ts };
  } catch (e) {
    return null; // storage que lança, JSON corrompido: começa do zero
  }
}

/** Grava o registro. `false` quando o storage não aceitou (nunca lança). */
export function gravarRegistro(storage: StorageMinimo | null, reg: RegistroFingerprint): boolean {
  try {
    if (!storage) return false;
    storage.setItem(CHAVE_FINGERPRINT, JSON.stringify(reg));
    // Confirma a escrita: webview que aceita `setItem` e não persiste existe
    // (é o caso que preocupa a recuperação inline do index.html).
    const lido = lerRegistro(storage);
    return !!lido && lido.id === reg.id;
  } catch (e) {
    return false;
  }
}

/**
 * 32 hex, o mesmo formato do `visitorId` do fingerprintjs. `crypto` quando
 * existe; `Math.random` como último recurso — é identidade de conveniência,
 * não segredo.
 */
export function gerarIdAleatorio(cryptoObj: any = (typeof crypto === 'undefined' ? null : crypto)): string {
  const HEX = '0123456789abcdef';
  try {
    if (cryptoObj && typeof cryptoObj.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      cryptoObj.getRandomValues(bytes);
      let s = '';
      for (let i = 0; i < bytes.length; i++) s += HEX[bytes[i] >> 4] + HEX[bytes[i] & 15];
      return s;
    }
  } catch (e) { /* cai no Math.random */ }
  let s = '';
  for (let i = 0; i < 32; i++) s += HEX[Math.floor(Math.random() * 16)];
  return s;
}

/** `name` de um erro qualquer, sem lançar. */
function classeDe(err: any): string {
  try {
    const n = err && err.name;
    return typeof n === 'string' && n ? n.slice(0, 40) : 'sem-nome';
  } catch (e) {
    return 'sem-nome';
  }
}

/**
 * Resolve o id do aparelho. Nunca rejeita.
 *
 * Ordem: storage → fingerprintjs (com prazo) → aleatório. O primeiro que
 * produzir id é gravado e vence; o que chegar depois é descartado.
 */
export function resolverFingerprint(deps: DepsResolucao): Promise<ResolucaoFingerprint> {
  const agora = deps.agora || (() => Date.now());
  const gerar = deps.gerar || gerarIdAleatorio;
  const prazoMs = typeof deps.prazoMs === 'number' ? deps.prazoMs : PRAZO_FINGERPRINT_MS;
  const agendar = deps.agendar || ((fn: () => void, ms: number) => setTimeout(fn, ms));
  const cancelar = deps.cancelar || ((h: any) => clearTimeout(h));
  const inicio = agora();

  // 1) Cache: o caminho de todo mundo depois da primeira visita.
  const emCache = lerRegistro(deps.storage);
  if (emCache) {
    return Promise.resolve({ id: emCache.id, origem: 'cache', ms: agora() - inicio, persistido: true });
  }

  return new Promise<ResolucaoFingerprint>(resolve => {
    let resolvido = false;   // a Promise já devolveu um id (memória ou gravado)
    let encerrado = false;   // já houve veredito DEFINITIVO (gravou, ou falhou de vez)
    let idDoPrazo: string | null = null; // o aleatório em memória, se o prazo estourou
    let timer: any = null;

    const montar = (origem: 'aleatorio' | 'fingerprintjs', id: string, persistido: boolean, motivo?: string, erro_classe?: string) => {
      const r: ResolucaoFingerprint = { id, origem, ms: agora() - inicio, persistido };
      if (motivo) r.motivo = motivo;
      if (erro_classe) r.erro_classe = erro_classe;
      return r;
    };

    const fechar = (origem: OrigemFingerprint, id: string, motivo?: string, erro_classe?: string) => {
      if (encerrado) return; // regra 3: o primeiro veredito definitivo vence
      encerrado = true;
      try { if (timer !== null) cancelar(timer); } catch (e) { /* timer já morreu */ }
      // Regra 3 também contra OUTRA aba que tenha gravado nesse meio-tempo.
      const jaGravado = lerRegistro(deps.storage);
      const persistido = jaGravado ? false : gravarRegistro(deps.storage, { id, origem, ts: agora() });
      if (resolvido) {
        // Chegou depois do prazo: a Promise já resolveu com o aleatório em
        // memória. Só avisa — a sessão corrente não troca de id.
        if (persistido && deps.aoGravarTardio) {
          const m = origem === 'fingerprintjs' ? 'tardio' : motivo;
          try { deps.aoGravarTardio(montar(origem, id, true, m, erro_classe)); } catch (e) { /* observador */ }
        }
        return;
      }
      resolvido = true;
      resolve(montar(origem, id, persistido, motivo, erro_classe));
    };

    const aleatorio = (motivo: string, err?: any) => {
      // Falha definitiva depois do prazo: grava o MESMO aleatório que a sessão
      // já está usando, para o id do pedido e o do storage coincidirem.
      let id: string;
      if (idDoPrazo) id = idDoPrazo;
      else { try { id = gerar(); } catch (e) { id = gerarIdAleatorio(); } }
      fechar('aleatorio', id, motivo, err === undefined ? undefined : classeDe(err));
    };

    // 5) Prazo: um import pendurado não segura o pedido — mas lentidão não é
    //    bloqueio, então o aleatório do prazo fica SÓ em memória. O que chegar
    //    depois (fingerprintjs, ou a falha definitiva dele) ainda passa por
    //    `fechar` e é o que decide o storage.
    try {
      timer = agendar(() => {
        if (resolvido || encerrado) return;
        resolvido = true;
        let id: string;
        try { id = gerar(); } catch (e) { id = gerarIdAleatorio(); }
        idDoPrazo = id;
        resolve(montar('aleatorio', id, false, 'prazo'));
      }, prazoMs);
    } catch (e) { timer = null; }

    // 2) fingerprintjs. Toda falha é um motivo distinto — no Athena eles
    //    separam "bloqueado" (import) de "biblioteca quebrada" (load/get).
    let promessa: Promise<string>;
    try {
      promessa = deps.carregar();
    } catch (e) {
      aleatorio('carregador', e);
      return;
    }
    Promise.resolve(promessa).then(
      id => {
        if (typeof id === 'string' && ID_VALIDO.test(id)) fechar('fingerprintjs', id);
        else aleatorio('vazio');
      },
      err => aleatorio(motivoDoErro(err), err),
    );
  });
}

/**
 * O carregador real embrulha cada etapa com um marcador para o motivo chegar
 * aqui; sem marcador, é o `import()` em si (o caso do bloqueador).
 */
export function motivoDoErro(err: any): string {
  try {
    const m = err && err.__mpEtapa;
    return typeof m === 'string' && m ? m : 'import';
  } catch (e) {
    return 'import';
  }
}

/** Marca em qual etapa um erro nasceu, sem perder o erro. */
export function marcarEtapa<T>(etapa: string, p: Promise<T>): Promise<T> {
  return p.catch(err => {
    try { if (err && typeof err === 'object') (err as any).__mpEtapa = etapa; } catch (e) { /* frozen */ }
    throw err;
  });
}
